import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'

import { Formik } from 'formik'
import * as Yup from 'yup'

import { InviteIchraEmployeesRequest, inviteIchraEmployees } from 'core'

import { Alert, Button, Col, DatePicker, DefaultCheckbox, Modal, Row } from 'common/components'
import { addDays, isTodayAfter, now, parseZone, parseZoneFormat, submitDateToServerFormat } from 'common/dateFormatters'
import { useCommandRequestV2 } from 'common/hooks'
import { ROUTE_PATHS } from 'common/routePaths'

import ConfirmBenefitLaunchModal from './confirmBenefitLaunchModal'

import './styles.less'

type InviteIchraEmployeesFormProps = {
  visible: boolean
  benefitStartDate: string
  notificationDate: string
  onCancel?: () => void
  onSendInvite?: () => void
}

type formData = {
  allEmployeesAdded: boolean
  benefitStartDate: any
  notificationDate: any
}

const InviteIchraEmployeesModal = (props: InviteIchraEmployeesFormProps) => {
  const { t: translate } = useTranslation()
  const history = useHistory()

  const [isVisibleConfirmModal, setIsVisibleConfirmModal] = useState<boolean>(false)
  const [isValidForm, setIsValidForm] = useState<boolean>()
  const [initialValues, setInitialValues] = useState<formData>({
    allEmployeesAdded: false,
    notificationDate: props.notificationDate,
    benefitStartDate: props.benefitStartDate,
  })

  const { sendCommand, isLoading } = useCommandRequestV2((request: InviteIchraEmployeesRequest) =>
    inviteIchraEmployees(request),
  )

  const isTodayAfterThanBenefitStartDate = isTodayAfter(props.benefitStartDate)
  const dayToCompare = parseZone(props.notificationDate)
  const [minBenefitStartDate, setMinBenefitStartDate] = useState(addDays(dayToCompare, 30))
  const [maxBenefitStartDate, setMaxBenefitStartDate] = useState(addDays(dayToCompare, 90))

  const onFormSubmit = async () => {
    const result = await sendCommand({
      notificationDate: submitDateToServerFormat(initialValues.notificationDate),
      planPeriodStartDate: submitDateToServerFormat(initialValues.benefitStartDate),
    })

    if (result.completed && props.onSendInvite) {
      props.onSendInvite()
      setIsVisibleConfirmModal(false)
    }
  }

  const handleConfirmModal = () => {
    setIsVisibleConfirmModal((prev) => !prev)
  }

  return (
    <Modal
      className={'invite-ichra-employees-modal-wrap'}
      onCancel={() => {
        if (props.onCancel) {
          props.onCancel()
        }
      }}
      visible={props.visible}
      title={'Confirm your Benefit Notification and Start Dates'}
      footer={
        <div>
          <Button
            onClick={() => {
              if (props.onCancel) {
                props.onCancel()
              }
            }}
          >
            {translate('general.cancel')}
          </Button>

          <Button onClick={handleConfirmModal} type="primary" disabled={!isValidForm} className="ml-8">
            Send invite
          </Button>
        </div>
      }
    >
      <Formik
        enableReinitialize={true}
        initialValues={initialValues}
        validationSchema={formValidator(translate)}
        onSubmit={onFormSubmit}
        validate={(values) => {
          setIsValidForm(formValidator(translate).isValidSync(values))
          setInitialValues(values)
          return {}
        }}
      >
        {(formik) => (
          <div className="invite_employees_content">
            <form onSubmit={formik.handleSubmit} autoComplete="off">
              {isTodayAfterThanBenefitStartDate && (
                <>
                  <p>
                    Your benefit start date: <b>{parseZoneFormat(props.benefitStartDate)}</b> is in the past!
                  </p>
                  <p>
                    In order to notify your employees please go to your{' '}
                    <a
                      href={''}
                      onClick={() => {
                        history.push(ROUTE_PATHS.BENEFIT_DETAILS_PATH)
                      }}
                    >
                      benefit details
                    </a>{' '}
                    and change the benefit start date.
                  </p>
                </>
              )}

              {!isTodayAfterThanBenefitStartDate && (
                <>
                  <p className={'mb-24'}>
                    PeopleKeep will send your employees an email informing them of their new ICHRA benefit.
                  </p>

                  <div className={'mb-32'}>
                    <div className={'mb-24'}>
                      <DatePicker
                        label={'ICHRA Employee Notification Date'}
                        name="notificationDate"
                        disabledDate={(d) => !d || d.isBefore(addDays(now(), -1)) || d.isAfter(addDays(now(), 90))}
                        helperText={
                          'If a new Notification Date is selected, your earliest Benefits Start Date will calculate automatically based on the new Notification Date. '
                        }
                        value={formik.values.notificationDate ? parseZone(formik.values.notificationDate) : null}
                        onChange={(dateAsString) => {
                          if (!dateAsString) {
                            formik.setValues({
                              allEmployeesAdded: formik.values.allEmployeesAdded,
                              notificationDate: null,
                              benefitStartDate: null,
                            })
                            return
                          }

                          const date = parseZone(dateAsString)
                          const benefitStartDate = addDays(date, 30)
                          /*
                           * I set the value for the notification date because i need a date object. Need to find a better way to do this
                           * */
                          formik.setValues({
                            allEmployeesAdded: formik.values.allEmployeesAdded,
                            notificationDate: date,
                            benefitStartDate: benefitStartDate,
                          })
                          setMinBenefitStartDate(benefitStartDate)
                          setMaxBenefitStartDate(addDays(date, 90))
                        }}
                      />
                    </div>

                    {formik.values.notificationDate && (
                      <>
                        <DatePicker
                          label={'ICHRA Benefits Start Date'}
                          name="benefitStartDate"
                          disabledDate={(d) => !d || d.isBefore(minBenefitStartDate) || d.isAfter(maxBenefitStartDate)}
                          value={formik.values.benefitStartDate ? parseZone(formik.values.benefitStartDate) : null}
                        />

                        <Alert
                          type="info"
                          showIcon
                          description={
                            'Your Benefit Start Date must be no less than 30 and no more than 90 calendar days from your ICHRA Notification Date'
                          }
                        />
                      </>
                    )}
                  </div>

                  <Row>
                    <Col span={24}>
                      <DefaultCheckbox
                        children={'I have added all employees eligible for this benefit'}
                        name="allEmployeesAdded"
                        className="no-select"
                      />
                    </Col>
                  </Row>

                  <Row className="mt-8">
                    <Col span={24}>
                      <div>
                        <p>
                          {translate('admin-dashboard.home.invite-alert.go-to')}
                          <a
                            href={`${ROUTE_PATHS.MANAGE_PEOPLE}?fromtask=true`}
                            rel="noreferrer"
                            className="link underline"
                          >
                            {translate('admin-dashboard.home.invite-alert.manage-people-link')}
                          </a>
                          {translate('admin-dashboard.home.invite-alert.last-part')}
                        </p>
                      </div>
                    </Col>
                  </Row>
                </>
              )}
            </form>
            <ConfirmBenefitLaunchModal
              visible={isVisibleConfirmModal}
              onCancel={() => setIsVisibleConfirmModal(false)}
              onSendInvite={() => {
                formik.submitForm()
              }}
              isLoading={isLoading}
            />
          </div>
        )}
      </Formik>
    </Modal>
  )
}

const formValidator = (translate: any) =>
  Yup.object({
    allEmployeesAdded: Yup.bool().oneOf([true], 'Required'),
    notificationDate: Yup.date().required(translate('general.required')).nullable().typeError(''),
    benefitStartDate: Yup.date().required(translate('general.required')).nullable().typeError(''),
  })

export default InviteIchraEmployeesModal
